import View from 'views/base/view';
import BlocksView from 'views/block/block-collection-view';
import ContentService from 'services/loader-handler-services/content-service';
import FeedbackTemplate from 'views/feedback/templates/feedback';
import Chaplin from 'chaplin';
import _ from 'lodash';
import $ from 'jquery';
import UserAgentService from 'services/browser-handler-services/user-agent-service';

// hidden aria-live region for screen reader announcements
const ANNOUNCER_ID = 'feedback-vo-announcer';

// Chrome + Mac VoiceOver needs programmatic focus, hidden live region alone is unreliable there
function isChromeOnMac() {
  const ua = navigator.userAgent || '';
  const isMac = !UserAgentService.isSafari() && /Mac|iPhone|iPad|iPod/i.test(navigator.platform || ua);
  const isChrome = /Chrome/i.test(ua) && !/Edg|OPR/i.test(ua);
  return isChrome && isMac;
}

// flat visible text from leaf nodes, skipping sr-only, aria-hidden, and display:none elements
function extractVisibleText($container) {
  return $container
    .find('*')
    .filter(function () {
      const $el = $(this);
      return (
        $el.children().length === 0 &&
        $el.closest('.sr-only, [aria-hidden="true"]').length === 0 &&
        $el.closest(':hidden').length === 0 &&
        $.trim($el.text()).length > 0
      );
    })
    .map(function () { return $.trim($(this).text()); })
    .get()
    .join(' ');
}

// Get or create the hidden aria-live region.
function getAnnouncer() {
  let $announcer = $(`#${ANNOUNCER_ID}`);
  if (!$announcer.length) {
    $announcer = $('<div>', {
      id: ANNOUNCER_ID,
      'aria-live': 'polite',
      'aria-atomic': 'true',
    }).addClass('sr-live-region').appendTo('#site-container');
  }
  return $announcer;
}

// clear-then-set so screen readers detect the change even when text is identical
// cancel any pending update before scheduling to prevent stale overwrites
let announceTimeoutId = null;
function announce(text) {
  const $announcer = getAnnouncer();
  $announcer.text('');
  clearTimeout(announceTimeoutId);
  announceTimeoutId = setTimeout(() => $announcer.text(text), 50);
}

const FeedbackView = View.extend({
  autoRender: true,
  autoAttach: true,
  region: 'feedback',
  noWrap: true,
  template: FeedbackTemplate,
  initialize: function (options) {
    this.model.set('cid', this.model.cid);
    if (this.model.get('class')) {
      this.className += this.model.get('class');
    }
    this.model.set('blocks', this.model.get('blocks'));
    this.callbacks = options.callbacks;
    this.script = options.script;
    const self = this;
    const role = this.model.get('role');
    if (!self.$el.attr('aria-hidden') && (role === null || role === undefined || role === '')) {
      this.model.set('role', 'alert');
    } else {
      this.model.set('role', false);
    }
    this.isChromeMac = isChromeOnMac();
    const describedby = this.model.get('describedby');
    if (describedby) {
      self.$el.attr('aria-describedby', describedby);
    }
    this.on('addedToDOM', function () {
      setTimeout(() => {
        const blocksRegion = `${this.model.get('cid')}_feedback_blocks`;
        this.registerRegion(blocksRegion, `.${blocksRegion}`);
        ContentService.buildBlockCollection(this.model.get('blocks')).then(blocks => {
          this.blocks = blocks;
          const blocksView = new BlocksView({
            script: this.script ? this.script.block : undefined,
            collection: this.blocks,
            region: blocksRegion,
            parent: this,
            pageView: options.pageView,
          });

          self.subview('blocks', blocksView);
          self.listenTo(blocksView, 'block:state:completed', self.blockCompleted);
          const onLoaded = () => {
            self.setState('loaded');
            self.announceFeedback();
          };
          if (blocksView.isLoaded()) {
            onLoaded();
          } else {
            self.listenTo(blocksView, 'block:state:loaded', onLoaded);
          }
          if (this.model.get('scroll_into_view')) {
            setTimeout(() => {
              self.el.focus({ preventScroll: true });
              self.el.scrollIntoView({ behavior: 'smooth' });
            }, 100);
          }
          // if there is a 'close' button, move focus on it when feedback is added to dom - accessibility issue
          if (self.$('[data-close]')) {
            self.$('[data-close]').trigger('focus');
          }
          self.delegate('click', '[data-close]', function (e) {
            e.preventDefault();
            self.trigger('feedback:close', this);
          });
        });
      }, 100);
    });

    this.setState('initialized');
    View.prototype.initialize.apply(this, arguments);
  },
  // pushes visible feedback text into the hidden aria-live region for screen reader announcement
  announceFeedback: function () {
    if (this.isChromeMac) {
      return;
    }
    const self = this;
    clearTimeout(self.announceFeedbackTimeoutId);
    self.announceFeedbackTimeoutId = setTimeout(() => {
      // Guard against the view being disposed before the timeout fires.
      if (!self.model) {
        return;
      }
      const blocksRegion = $(`.${self.model.get('cid')}_feedback_blocks`);
      if (!blocksRegion.length) {
        return;
      }
      announce(extractVisibleText(blocksRegion));
    }, 300);
  },
  play: function () {
    this.setState('playing');
    if (this.blocks && this.blocks.length === 0) {
      this.setState('completed');
    }
    this.focusPostFeedback();
  },
  blockCompleted: function () {
    if (this.subviewsByName.blocks.isComplete()) {
      this.setState('completed');
    }
  },
  stateCallbacks: {
    unloading: function () {
      this.setState('unloaded');
      View.prototype.dispose.apply(this, arguments);
    },
    completed: function () {
      if (this.model && this.model.get('closeOnComplete')) {
        this.trigger('feedback:close', this);
      }
    },
  },
  setState: function (state) {
    this.model.set('state', state);
    //feedback is kinda like a mini page - needs to talk to blocks the same way
    Chaplin.mediator.publish(`feedback:state:${this.model.get('state')}`, this);

    const hook = this.script && this.script[state] ? _.bind(this.script[state], this.script) : null;
    const stateCallback = this.stateCallbacks[state] ? _.bind(this.stateCallbacks[state], this) : null;
    const callback = this.callbacks && this.callbacks[state] ? _.bind(this.callbacks[state], this) : null;

    const callCallbacks = function () {
      if (callback) {
        callback();
      }
      if (stateCallback) {
        stateCallback();
      }
    };

    if (hook) {
      if (this.script[state].length === 2) {
        hook(this, callCallbacks);
      } else {
        hook(this);
        callCallbacks();
      }
    } else {
      callCallbacks();
    }
    this.trigger('page:state:change', this);
  },
  // announcement handled by announceFeedback() for most browsers
  // Chrome + macOS still needs focus-based approach for VoiceOver
  focusPostFeedback() {
    if (!this.isChromeMac) {
      return;
    }
    const region = this.region;
    if (typeof region !== 'string') {
      return;
    }
    if (region !== 'one-at-a-time-feedback-region' && !region.startsWith('question-feedback-')) {
      return;
    }
    const blocksRegion = $(`.${this.model.get('cid')}_feedback_blocks`);
    blocksRegion.attr('tabindex', 0).trigger('focus');
    blocksRegion.on('focusout', () => {
      if (blocksRegion.attr('tabindex')) {
        blocksRegion.removeAttr('tabindex');
      }
    });
  },
  dispose: function () {
    clearTimeout(this.announceFeedbackTimeoutId);
    this.setState('unloading');
  },
});

export default FeedbackView;
